import React from 'react';

const HumanoidRobotics = () => (
  <article className="news-article-body">
    <p className="news-lead">
      Something shifted in humanoid robotics between 2024 and 2025. The machines stopped
      looking like expensive research demonstrations and started looking like products.
      Boston Dynamics' Atlas is doing warehouse tasks. Tesla's Optimus is assembling cars.
      Unitree's G1 costs $16,000. And NVIDIA open-sourced a foundation model for robot
      control that any lab can build on. The "robots are always five years away" joke
      is getting harder to tell.
    </p>

    <h2>Unitree - the affordable humanoid</h2>
    <p>
      Unitree Robotics, a Chinese company founded in 2016, has taken a different path
      than Boston Dynamics. Instead of building expensive bespoke systems for research labs,
      Unitree focused relentlessly on bringing costs down.
    </p>
    <p>
      The Unitree G1, launched in mid-2024, is a full humanoid robot (1.32m tall, 35kg)
      priced at $16,000 - a fraction of what comparable systems cost. Key capabilities:
    </p>
    <ul>
      <li>Dexterous hand manipulation - pick up objects, open doors, use tools</li>
      <li>Dynamic locomotion - walks, runs, climbs stairs, recovers from pushes</li>
      <li>Developer-accessible: Python SDK, ROS2 support, open joint control API</li>
    </ul>
    <p>
      The H1 Pro, their higher-end model, has been demonstrated performing tasks like
      backflips, sparring, and basic household work. The videos go viral. The underlying
      point - that affordable, capable humanoid hardware exists - is serious.
    </p>

    <h2>Boston Dynamics Atlas - from demo to work</h2>
    <p>
      Boston Dynamics retired the hydraulic Atlas in April 2024 and unveiled the fully
      electric version. The new Atlas is lighter, stronger, and significantly more flexible
      than its predecessor - with a range of motion that exceeds the human body in several
      joints.
    </p>
    <p>
      More importantly, Hyundai (which owns Boston Dynamics) has been deploying Atlas in
      automotive manufacturing pilots. Atlas can pick and place car parts, operate in
      shared spaces with human workers, and has enough dexterity for assembly tasks
      traditionally requiring human hands. The gap between "research demo" and "working
      in a factory" is closing.
    </p>

    <h2>Tesla Optimus - vertical integration advantage</h2>
    <p>
      Tesla's Optimus Gen 2, demonstrated in late 2024, can fold laundry, sort objects
      by colour, and perform basic assembly tasks in Tesla's own factories. Elon Musk has
      claimed production will scale to 1 million units per year eventually - a target most
      analysts view with extreme scepticism, but one that signals Tesla's serious intent.
    </p>
    <p>
      Tesla's advantage is vertical integration: they design the silicon (Full Self-Driving
      chip adapted for robotics), the actuators, and the training infrastructure. They can
      iterate faster because they control every component.
    </p>

    <h2>NVIDIA Isaac GR00T - the open-source foundation model</h2>
    <p>
      In March 2025, NVIDIA open-sourced Isaac GR00T (Generalist Robot 00 Technology) -
      a foundation model for humanoid robot control. Think of it as the "base model" for
      robotics, the way Llama is a base model for language.
    </p>
    <p>
      GR00T is trained on both real robot demonstrations and synthetic data generated
      in NVIDIA's Isaac Lab simulation environment. Key components:
    </p>
    <ul>
      <li><strong>GR00T-N1:</strong> The core vision-language-action model - understands what it sees and plans actions in response</li>
      <li><strong>Isaac Lab:</strong> Open-source simulation environment for generating synthetic training data</li>
      <li><strong>Omniverse NuRec:</strong> Video understanding model trained on human movement for better motion synthesis</li>
      <li><strong>Newton:</strong> NVIDIA's physics engine optimised for robotics simulation</li>
    </ul>
    <p>
      The open-source strategy mirrors Meta's Llama approach: build a capable foundation
      model, release it openly, build an ecosystem around NVIDIA's compute.
    </p>

    <h2>Figure AI and the VC-funded humanoid wave</h2>
    <p>
      Figure AI raised $675 million in early 2024, with backing from Microsoft, NVIDIA,
      Amazon, and OpenAI. Their Figure 02 has been demonstrated performing BMW factory
      assembly tasks with notable dexterity. The company is one of several well-funded
      startups (Apptronik, 1X, Agility Robotics) betting that the convergence of better
      AI and cheaper hardware makes humanoid robots commercially viable now.
    </p>

    <div className="news-insight-box">
      <h3>The big lesson</h3>
      <p>
        The humanoid robotics space has three converging factors making 2025 a real inflection
        point: AI models capable of understanding instructions and planning actions, hardware
        costs dropping rapidly (Unitree G1 at $16K proves this), and major companies deploying
        in real production environments. NVIDIA's open-source GR00T model could accelerate
        all of this by giving every robotics lab a starting point rather than requiring
        each to build from scratch. The robots are no longer five years away.
      </p>
    </div>
  </article>
);

export default HumanoidRobotics;
